"use client";

import { forwardRef, useState } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

type BaseProps = Omit<ComponentPropsWithoutRef<"input">, "className">;

export interface AuthInputProps extends BaseProps {
  label: string;
  error?: string | null;
  rightSlot?: ReactNode;
}

export const AuthInput = forwardRef<HTMLInputElement, AuthInputProps>(function AuthInput(
  { label, error, rightSlot, type = "text", id, ...rest },
  ref,
) {
  const [showPw, setShowPw] = useState(false);
  const inputId = id ?? rest.name ?? label.toLowerCase().replace(/\s+/g, "-");
  const isPassword = type === "password";
  const effectiveType = isPassword && showPw ? "text" : type;

  return (
    <div className="flex flex-col gap-2 text-left">
      <div className="flex items-center justify-between">
        <label
          htmlFor={inputId}
          className="text-[13px] font-medium text-ink-dim"
        >
          {label}
        </label>
        {rightSlot}
      </div>
      <div
        className={cn(
          "relative flex items-center rounded-full border bg-surface transition-colors",
          "border-line focus-within:border-line-strong",
          error && "border-red-500/60 focus-within:border-red-500/80",
        )}
      >
        <input
          ref={ref}
          id={inputId}
          type={effectiveType}
          className={cn(
            "h-12 w-full rounded-full bg-transparent text-[15px] text-ink placeholder:text-ink-faint outline-none",
            "px-5",
            isPassword && "pr-12",
          )}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPw((v) => !v)}
            aria-label={showPw ? "Hide password" : "Show password"}
            className="absolute right-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-mute transition-colors hover:text-ink"
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
});
